import { NOLEGGIATORI } from './constants'
import toaster from './toaster';
import i18next from 'i18next';

/** Nome commerciale del noleggiatore Quicko, come mostrato nella tabella offerte. */
export const quickoNoleggiatoreLabel = (noleggiatore: string): string => {
  const val = noleggiatore.toUpperCase();
  if (val === NOLEGGIATORI.LEASYS_NOLEGGIO_CHIARO) return 'LEASYS RENT&BUY';
  if (val === NOLEGGIATORI.ALD) return 'AYVENS';
  return val;
};

export const getCheckedServices = (values: any) => {
  const checkedServices = Object.entries(values).flatMap(([key, value]: [any, any]) => {
    const isServicesOptions = (val: any): any => val && 'isChecked' in val && 'label' in val;
    if (Array.isArray(value)) {
      return value.filter(option => option.isChecked).map(option => `${option.labelRiepilogo}`);
    } else if (isServicesOptions(value)) {
      return value.isChecked ? [`${value.label}: ${value.value || ''}`.trim()] : [];
    } else {
      return [];
    }
  });
  return checkedServices
}

/**
 * Piano di noleggio e servizi di un preventivo Quicko, nei parametri GTM.
 * Viaggiano con l'evento CTA_download_preventivo_Quicko (vedi PdfStore).
 */
export const quickoGtmQuotationData = (payload: any, services: any) => ({
  mobility_services: {
    Manutenzione: 'Si',
    Gomme: {
      quality_performance:
        payload.tipo_gomma === 'quality'
          ? `Treni Gomme estive ${payload.n_treno_gomme_estive} - Treni Gomme invernali ${payload.n_treno_gomme_invernali}`
          : 'No',
      premium:
        payload.tipo_gomma === 'premium'
          ? `Treni Gomme estive ${payload.n_treno_gomme_estive} - Treni Gomme invernali ${payload.n_treno_gomme_invernali}`
          : 'No',
      gomme_illimitate: payload.gomme_illimitate_YN ? 'Si' : 'No',
    },
    Vettura_Sostitutiva: payload.auto_sostitutiva_gruppo || 'No',
  },
  insurance_services: {
    RCA:
      services.assicurazioneRC.find((option) => option.isChecked)?.label ||
      'No',
    Kasko: `${payload.kasko_franchigia}€`,
    theft_fire: `${payload.incendio_furto_scoperto_perc}%`,
    legal_protection: payload.tutela_legale_YN ? 'Si' : 'No',
  },
  rental_duration: payload.durata,
  km_years: payload.km_annui,
  advance_IVA: payload.anticipo_no_iva,
});

export const recalculateQuickoQuotation = (row, rentalDuration, existDiscount, setQuotations, leasesplan_min_value = 3.5, leaseplan_max_value = 10) => {
  // this calculations have been moved to the BE, keep this function in case one day need to use it again
  // wrap it in a useCallback
  const currentRow = row.original;
  let newCanone = 0;
  let newPercentage = 0;
  let newCommissioni = 0;

  const { inputCommissioni, originalCanone, discountedCanone, fullPriceVehicle } = currentRow;
  const baseCanone = existDiscount ? discountedCanone : originalCanone;
  const inputType = currentRow.typeInput;

  if (inputCommissioni) {
    if (inputType === "€") {
      // ARVAL LEASEPLAN calc on canone totale
      if (row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.ARVAL_PRIVATI.toLowerCase() ||
        row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.ARVAL_P_IVA.toLowerCase() ||
        row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.LEASEPLAN.toLowerCase()) {

        newCanone = (inputCommissioni / rentalDuration) + baseCanone;
        newPercentage = (1 - (newCanone / baseCanone)) * -100;

        if (row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.LEASEPLAN.toLowerCase()) {
          const maxValue = (baseCanone * rentalDuration * leaseplan_max_value) / 100;
          const minValue = (baseCanone * rentalDuration * leasesplan_min_value) / 100;

          if (inputCommissioni > maxValue || inputCommissioni < minValue) {
            toaster.error(i18next.t("values_between", { minValue: minValue.toFixed(2), maxValue: maxValue.toFixed(2), symbol: '€' }));
            return;
          }
        }
      } else if (row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.ALD.toLowerCase() ||
        row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.LEASYS_I_CARE.toLowerCase()) {
        newCanone = (inputCommissioni / rentalDuration) + baseCanone;
        newPercentage = (inputCommissioni / fullPriceVehicle) * 100;
      }

      if (newPercentage > 10) {
        toaster.error(i18next.t("percentage_not_gt_10", { newPercentage: newPercentage.toFixed(2) }));
        // toaster.error(`La percentuale di commissioni non può superare il 10%. Attualmente è al ${newPercentage.toFixed(2)}%`);
        return;
      }

      currentRow.canone = parseFloat(newCanone.toFixed(2));
      currentRow.commissioni = parseFloat(inputCommissioni.toFixed(2));
      currentRow.percentage = parseFloat(newPercentage.toFixed(2));

      setQuotations(prevQuotations => prevQuotations.map((quotation, index) => (index === row.index ? currentRow : quotation)));
    } else if (inputType === "%") {
      // ARVAL LEASEPLAN calc on canone totale
      if (row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.ARVAL_PRIVATI.toLowerCase() ||
        row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.ARVAL_P_IVA.toLowerCase() ||
        row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.LEASEPLAN.toLowerCase()) {
        newCommissioni = (baseCanone * rentalDuration * inputCommissioni) / 100;
        newCanone = (newCommissioni / rentalDuration) + baseCanone;

        if (row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.LEASEPLAN.toLowerCase()) {
          if (inputCommissioni > leaseplan_max_value || inputCommissioni < leasesplan_min_value) {
            toaster.error(i18next.t("values_between", { minValue: leasesplan_min_value, maxValue: leaseplan_max_value, symbol: '%' }));
            // toaster.error(`Il valore deve essere compreso fra ${leasesplan_min_value}% e ${leaseplan_max_value}%`);
            return;
          }
        }
      } else if (row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.ALD.toLowerCase() ||
        row.original.noleggiatore.toLowerCase() === NOLEGGIATORI.LEASYS_I_CARE.toLowerCase()) {
        newCommissioni = (fullPriceVehicle * inputCommissioni) / 100;
        newCanone = (newCommissioni / rentalDuration) + baseCanone;
      }

      currentRow.canone = parseFloat(newCanone.toFixed(2));
      currentRow.commissioni = parseFloat(newCommissioni.toFixed(2));
      currentRow.percentage = parseFloat(inputCommissioni.toFixed(2));

      setQuotations(prevQuotations => prevQuotations.map((quotation, index) => (index === row.index ? currentRow : quotation)));
    }

    const inputElement = document.getElementById(`input-${row.id}`) as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  }
};

export const clearAllInputs = (quotations) => {
  quotations.forEach((quotation, index) => {
    const inputElement = document.getElementById(`input-${index}`) as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  });
};

export const prepareNoleggiatoreBackend = (noleggiatore) => {
  return noleggiatore.replace(/[\s,-.]+/g, '_');
}

export const computeFadInfo = (prezzoListino: number, optionalTotals: number) => {
  const totalBeforeDiscount = prezzoListino + optionalTotals;
  //  (Listino + Optional) − 12,5%
  const fascia = totalBeforeDiscount - (totalBeforeDiscount * 0.125);
  let fadInfo: any = null;

  if (fascia > 10000 && fascia <= 15000) {
    fadInfo = { fad: 15, maxCommissionsPerc: 5 };
  } else if (fascia > 15000 && fascia <= 20000) {
    fadInfo = { fad: 15, maxCommissionsPerc: 5 };
  } else if (fascia > 20000 && fascia <= 30000) {
    fadInfo = { fad: 25, maxCommissionsPerc: 4.50 };
  } else if (fascia > 30000 && fascia <= 40000) {
    fadInfo = { fad: 35, maxCommissionsPerc: 4.50 };
  } else if (fascia > 40000 && fascia <= 50000) {
    fadInfo = { fad: 55, maxCommissionsPerc: 4 };
  } else if (fascia > 50000 && fascia <= 60000) {
    fadInfo = { fad: 65, maxCommissionsPerc: 4 };
  }  else if (fascia > 60000) {
    fadInfo = { fad: 80, maxCommissionsPerc: 3.5 };
  }

  console.debug('[FAD]', {
    prezzoListino,
    optionalTotals,
    'prezzoListino + optionalTotals' : totalBeforeDiscount,
    '12,5%': (totalBeforeDiscount * 0.125),
    '(prezzoListino + optionalTotals) - 12,5%': fascia,
    fascia,
    fadInfo
  })
  return fadInfo;
}

export const computeArvalWithFad = ({
  offer,
  prezzo_listino,
  optionalTotals,
}) => {
  const originalCanone = offer.updated_canone;
  const originalCommissions = offer.updated_commissioni;
  const fadInfo = computeFadInfo(prezzo_listino, optionalTotals);
  const commissionsPerc = offer.percentage;

  const warning =
    commissionsPerc > fadInfo.maxCommissionsPerc
      ? `Per poter applicare lo sconto Horizon devi impostare le provvigioni fino al ${fadInfo.maxCommissionsPerc}%`
      : null;

  if (warning) return { warning };

  // Canone − [FAD + (Canone × 1%)]
    const finalCanone = originalCanone - ((fadInfo.fad || 0) + originalCanone * 0.01);

  const overrides = {
    updated_canone: finalCanone,
    _original_canone: originalCanone,
    _original_commissioni: originalCommissions,
  };

  return { warning, overrides };
};

